import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { transportApi } from '@/services/transportApi';

const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; } };

const History = ({ edits }) => (!edits?.length ? null : (
  <div className="border-t pt-2 space-y-1" data-testid="odo-fix-history">
    <p className="text-[11px] font-semibold text-gray-500">Earlier corrections</p>
    {edits.map((e, i) => (
      <p key={i} className="text-[11px] text-gray-600" data-testid={`odo-fix-history-${i}`}>
        {fmt(e.at)} · {e.by}: start {e.before?.start_odometer ?? '—'} → {e.after?.start_odometer ?? '—'}, end {e.before?.end_odometer ?? '—'} → {e.after?.end_odometer ?? '—'} · “{e.reason}”
      </p>
    ))}
  </div>
));

/** Manager: correct a trip's start / end odometer reading (reason required, every change kept). */
export const OdoFixDialog = ({ trip, onClose, onDone }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!trip) return;
    setStart(String(trip.start_odometer ?? '')); setEnd(trip.end_odometer != null ? String(trip.end_odometer) : ''); setReason(''); setLink(null);
    transportApi.odoLink(trip.id).then(setLink).catch(() => {});
  }, [trip]);
  const ended = trip?.end_odometer != null;
  const dist = ended && start !== '' && end !== '' ? Math.round((Number(end) - Number(start)) * 10) / 10 : null;
  const belowPrev = link?.last_end != null && start !== '' && Number(start) < link.last_end;

  const save = async () => {
    if (start === '' || isNaN(Number(start)) || (ended && (end === '' || isNaN(Number(end))))) { toast.error('Enter the readings'); return; }
    if (dist != null && dist < 0) { toast.error('End reading cannot be lower than the start reading'); return; }
    if (reason.trim().length < 3) { toast.error('Enter a reason for the correction'); return; }
    setBusy(true);
    try {
      await transportApi.fixOdometer(trip.id, { start_odometer: Number(start), end_odometer: ended ? Number(end) : undefined, reason: reason.trim() });
      toast.success('Odometer readings corrected'); onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Correction failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="odo-fix-dialog">
        <DialogHeader>
          <DialogTitle>Correct odometer readings</DialogTitle>
          <DialogDescription>{trip?.route_summary || `${trip?.pickup || '—'} → ${trip?.drop || '—'}`} · {trip?.vehicle_label}</DialogDescription>
        </DialogHeader>
        {link?.last_end != null && (
          <p className="text-xs text-gray-600" data-testid="odo-fix-prev-end">Previous trip on this vehicle ended at <b>{link.last_end} km</b>{link.last_trip?.end_time ? ` · ${fmt(link.last_trip.end_time)}` : ''}</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Start reading (km)</Label><Input type="number" value={start} onChange={(e) => setStart(e.target.value)} data-testid="odo-fix-start-input" /></div>
          <div><Label>End reading (km)</Label><Input type="number" value={end} onChange={(e) => setEnd(e.target.value)} disabled={!ended} placeholder={ended ? '' : 'Trip not ended'} data-testid="odo-fix-end-input" /></div>
        </div>
        {belowPrev && <p className="text-xs text-amber-700" data-testid="odo-fix-below-prev">Start is lower than the previous trip's end ({link.last_end} km) — double-check.</p>}
        {dist != null && <p className={`text-sm ${dist < 0 ? 'text-red-600' : 'text-gray-700'}`} data-testid="odo-fix-distance">Distance: <b>{dist} km</b>{trip?.distance != null && ` (was ${trip.distance} km)`}</p>}
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required) — e.g. AI misread the start photo" rows={2} data-testid="odo-fix-reason" />
        <History edits={trip?.odo_edits} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="odo-fix-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="odo-fix-save">{busy ? 'Saving…' : 'Save correction'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OdoFixDialog;
